<script setup>
import { ref, watch } from 'vue'
import { useThumbnailsStore } from '@/stores/thumbnails'
import { useConfigurationStore } from '@/stores/configuration'
import { useAlertsStore } from '@/stores/alerts'
import { ensureLargeCachedUrl } from '@/utils/common'
import ThumbnailImage from '@/components/Global/ThumbnailImage.vue'
import ImageAnalysisView from '../../views/ImageAnalysisView.vue'

const props = defineProps({
  images: {
    type: Array,
    default: () => []
  },
  selectedImages: {
    type: Array,
    default: () => []
  },
  columnSpan: {
    type: Number,
    required: true
  },
  allowSelection: {
    type: Boolean,
    default: false
  },
  enableImageCards: {
    type: Boolean,
    default: true
  }
})

const configurationStore = useConfigurationStore()
const alertsStore = useAlertsStore()
const thumbnailsStore = useThumbnailsStore()
const emit = defineEmits(['selectImage', 'toggleSelection'])
const showAnalysisDialog = ref(false)
const imageDetails = ref({})
const analysisImage = ref({})

const launchAnalysis = async (image) => {
  alertsStore.setAlert('info', `Opening ${image?.basename} for analysis`)
  try {
    await ensureLargeCachedUrl(image, thumbnailsStore.cacheImage, configurationStore.archiveType)
    analysisImage.value = image
    showAnalysisDialog.value = true
  } catch {
    alertsStore.setAlert('error', `Failed to open ${image?.basename}`)
  }
}

async function showAdjacentImage(direction) {
  if (!props.images.length) return

  const currentIndex = props.images.findIndex((image) => image.basename === analysisImage.value?.basename)
  if (currentIndex < 0) return

  const nextIndex = (currentIndex + direction + props.images.length) % props.images.length
  const nextImage = props.images[nextIndex]

  await ensureLargeCachedUrl(nextImage, thumbnailsStore.cacheImage, configurationStore.archiveType)
  analysisImage.value = nextImage
}

const isSelected = (basename) => {
  return props.selectedImages.includes(basename)
}

watch(() => props.images, () => {
  props.images.forEach(image => {
    if (image.basename && !(image.basename in imageDetails.value)) {
      imageDetails.value[image.basename] = ref('')
      const url = image.smallThumbUrl || image.small_url ||''
      thumbnailsStore.cacheImage('small', configurationStore.archiveType, url, image.basename).then((cachedUrl) => {
        imageDetails.value[image.basename] = cachedUrl
      })
    }
  })
}, { deep: true, immediate: true })

</script>

<template>
  <v-row @dblclick="emit('toggleSelection')">
    <v-col
      v-for="(image, index) in props.images"
      :key="index"
      :cols="columnSpan"
      class="image-grid-col"
    >
      <thumbnail-image
        :image="image"
        :image-url="imageDetails[image.basename]"
        :is-selected="isSelected(image.basename)"
        :enable-image-cards="true"
        :enable-removal="false"
        @select-image="emit('selectImage', image.basename)"
        @launch-analysis="launchAnalysis(image)"
      />
    </v-col>
  </v-row>
  <v-dialog
    v-model="showAnalysisDialog"
    fullscreen
  >
    <image-analysis-view
      :image="analysisImage"
      @close-analysis-dialog="showAnalysisDialog = false"
      @request-previous-image="showAdjacentImage(-1)"
      @request-next-image="showAdjacentImage(1)"
    />
  </v-dialog>
</template>

<style scoped>
.selected-image {
  outline: 0.3rem solid var(--primary-interactive);
}
.image-text-overlay {
  color: var(--text);
  font-weight: bold;
  margin-right: 5px;
  float: right;
}
.image-grid-col {
  max-width: 200px;
  min-width: 120px;
}
</style>
